/**
 * Session storage for the admin dashboard's own token — wholly separate from the CDP bearer
 * token bridge (`coinbase/accessTokenBridge.ts`) used by the rest of the app. `sessionStorage`
 * (not `localStorage`) is a deliberate middle ground: it survives a page refresh within the same
 * tab (so the admin isn't logged out by every navigation) but is cleared when the tab closes,
 * which bounds how long a token can sit around if the machine is shared or compromised — a
 * meaningfully more sensitive credential than an ordinary end-user session.
 */

const TOKEN_KEY = "wicked_admin_token";
const EXPIRES_KEY = "wicked_admin_expires_at";

export function setAdminSession(token: string, expiresAt: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(EXPIRES_KEY, expiresAt);
}

export function clearAdminSession(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(EXPIRES_KEY);
}

/** Returns the token only if present and not (locally) expired — this is a UX convenience to
 *  avoid firing an obviously-stale request, not a security boundary; the server independently
 *  verifies every token's signature and expiry on every request regardless. */
export function getAdminToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const expiresAt = sessionStorage.getItem(EXPIRES_KEY);
  if (!token || !expiresAt) return null;
  if (new Date(expiresAt).getTime() <= Date.now()) {
    clearAdminSession();
    return null;
  }
  return token;
}

export function isAdminAuthed(): boolean {
  return getAdminToken() !== null;
}
